import { chartPointToPixel } from "./coordinateUtils";
import { resolveDrawingStyle } from "./drawingStyleRegistry";
import { toOverlayPrimitive, readGhostData, readPatternMatches } from "./overlayPrimitiveAdapter";
import { calculateParallelChannelGeometry } from "./builtin/parallelChannel";
import { calculatePitchforkGeometry } from "./builtin/pitchfork";
import { calculateAbcdPatternMetrics } from "./builtin/abcdPattern";
import { calculateFibArcGeometry } from "./builtin/fibArc";
import { calculateFibTimeZoneGeometry } from "./builtin/fibTimeZone";
import { calculateRegressionChannelMetrics } from "./builtin/regressionChannel";
import { calculateGannAdvancedGeometry } from "./builtin/gannAdvanced";
import { clipSegmentToBox } from "./renderCanvas";
import { hitTestOverlayPrimitive, computeTextBoxLayout, } from "@vninvestcharts/engine-core";
export const HIT_TOLERANCE = {
    mouse: 6,
    touch: 16,
    pen: 8,
};
const AABB_SAFE_TYPES = new Set([
    "trendLine",
    "arrow",
    "hLine",
    "vLine",
    "rectangle",
    "dateAndPriceRange",
    "ellipse",
    "triangle",
    "polyline",
    "channel",
    "parallelChannel",
    "elliottImpulse",
    "elliottCorrection",
    "elliottTriangle",
    "elliottDoubleCombo",
    "elliottTripleCombo",
    "xabcdPattern",
    "cypherPattern",
    "headAndShoulders",
    "trianglePattern",
    "threeDrivesPattern",
    "abcdPattern",
    // CE24
    "crossLine",
    "datePriceRange",
]);
function toPixel(point, scales) {
    return chartPointToPixel(point, scales);
}
function distanceToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0) {
        return Math.hypot(px - x1, py - y1);
    }
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}
function pointInRect(px, py, rx, ry, rw, rh) {
    return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}
// S2/BUG-3: single date→ms conversion (was a triplicated ternary).
function dateToTimeMs(date) {
    return typeof date === "number" ? date : date.getTime();
}
// S2/BUG-3: lower-bound binary search (first index with times[i] >= target).
// O(log N) per match instead of O(N) findIndex → O(M+N) total with precomputed times.
function bisectLeft(times, target) {
    let lo = 0;
    let hi = times.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (times[mid] < target)
            lo = mid + 1;
        else
            hi = mid;
    }
    return lo < times.length ? lo : -1;
}
function sign(px, py, x1, y1, x2, y2) {
    return (px - x2) * (y1 - y2) - (x1 - x2) * (py - y2);
}
function distanceToPolyline(points, px, py) {
    if (points.length < 2) {
        return Number.POSITIVE_INFINITY;
    }
    let best = Number.POSITIVE_INFINITY;
    for (let index = 1; index < points.length; index += 1) {
        best = Math.min(best, distanceToSegment(px, py, points[index - 1].x, points[index - 1].y, points[index].x, points[index].y));
    }
    return best;
}
function pathBounds(points) {
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
function getTextBounds(drawing, scales) {
    const point = drawing.points[0];
    if (!point) {
        return null;
    }
    const pixel = toPixel(point, scales);
    const fontSize = drawing.style.fontSize ?? 12;
    const text = drawing.text?.trim().length ? drawing.text : "Text";
    const width = Math.max(fontSize, text.length * fontSize * 0.6);
    return {
        x: pixel.x - width * 0.1,
        y: pixel.y - fontSize * 0.5,
        width,
        height: fontSize,
    };
}
function hitTestLineLike(drawing, mouseX, mouseY, scales, chartWidth, chartHeight, tolerance) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    return distanceToSegment(mouseX, mouseY, start.x, start.y, end.x, end.y) <= tolerance;
}
function hitTestRayLike(drawing, mouseX, mouseY, scales, chartWidth, chartHeight, tolerance, multiplier, forwardOnly) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const clipped = forwardOnly
        ? clipSegmentToBox(start, { x: start.x + (end.x - start.x) * multiplier, y: start.y + (end.y - start.y) * multiplier }, chartWidth, chartHeight)
        : clipSegmentToBox({ x: start.x - (end.x - start.x) * multiplier, y: start.y - (end.y - start.y) * multiplier }, { x: start.x + (end.x - start.x) * multiplier, y: start.y + (end.y - start.y) * multiplier }, chartWidth, chartHeight);
    if (!clipped) {
        return false;
    }
    return distanceToSegment(mouseX, mouseY, clipped[0].x, clipped[0].y, clipped[1].x, clipped[1].y) <= tolerance;
}
function hitTestRectangleLike(drawing, mouseX, mouseY, scales, tolerance) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    if (pointInRect(mouseX, mouseY, x - tolerance, y - tolerance, width + tolerance * 2, height + tolerance * 2)) {
        return true;
    }
    return distanceToSegment(mouseX, mouseY, x, y, x + width, y) <= tolerance
        || distanceToSegment(mouseX, mouseY, x + width, y, x + width, y + height) <= tolerance
        || distanceToSegment(mouseX, mouseY, x + width, y + height, x, y + height) <= tolerance
        || distanceToSegment(mouseX, mouseY, x, y + height, x, y) <= tolerance;
}
function hitTestFibonacciLike(drawing, mouseX, mouseY, scales, tolerance, levels) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const priceDelta = endPoint.y - startPoint.y;
    const pixelDelta = end.y - start.y;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    return levels.some((level) => {
        const y = start.y + pixelDelta * level;
        return Math.abs(mouseY - y) <= tolerance && mouseX >= minX - tolerance && mouseX <= maxX + tolerance;
    });
}
function hitTestChannelLike(drawing, mouseX, mouseY, scales, tolerance) {
    const [startPoint, endPoint, offsetPoint] = drawing.points;
    if (!startPoint || !endPoint || !offsetPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const offsetAnchor = toPixel(offsetPoint, scales);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    const offsetVector = length === 0
        ? { x: offsetAnchor.x - start.x, y: offsetAnchor.y - start.y }
        : (() => {
            const normal = { x: -dy / length, y: dx / length };
            const distance = (offsetAnchor.x - start.x) * normal.x + (offsetAnchor.y - start.y) * normal.y;
            return { x: normal.x * distance, y: normal.y * distance };
        })();
    const offsetStart = { x: start.x + offsetVector.x, y: start.y + offsetVector.y };
    const offsetEnd = { x: end.x + offsetVector.x, y: end.y + offsetVector.y };
    return distanceToSegment(mouseX, mouseY, start.x, start.y, end.x, end.y) <= tolerance
        || distanceToSegment(mouseX, mouseY, offsetStart.x, offsetStart.y, offsetEnd.x, offsetEnd.y) <= tolerance;
}
function hitTestParallelChannel(drawing, mouseX, mouseY, scales, chartWidth, chartHeight, tolerance) {
    const geometry = calculateParallelChannelGeometry(drawing, scales);
    if (!geometry) {
        return false;
    }
    const upper = clipSegmentToBox({ x: geometry.start.x + geometry.offsetVector.x, y: geometry.start.y + geometry.offsetVector.y }, { x: geometry.end.x + geometry.offsetVector.x, y: geometry.end.y + geometry.offsetVector.y }, chartWidth, chartHeight);
    const middle = clipSegmentToBox(geometry.start, geometry.end, chartWidth, chartHeight);
    const lower = clipSegmentToBox({ x: geometry.start.x - geometry.offsetVector.x, y: geometry.start.y - geometry.offsetVector.y }, { x: geometry.end.x - geometry.offsetVector.x, y: geometry.end.y - geometry.offsetVector.y }, chartWidth, chartHeight);
    return [upper, middle, lower].some((segment) => segment != null && distanceToSegment(mouseX, mouseY, segment[0].x, segment[0].y, segment[1].x, segment[1].y) <= tolerance);
}
function hitTestPitchfork(drawing, mouseX, mouseY, scales, chartWidth, chartHeight, tolerance) {
    const geometry = calculatePitchforkGeometry(drawing, scales);
    if (!geometry || (geometry.direction.x === 0 && geometry.direction.y === 0)) {
        return false;
    }
    const median = clipSegmentToBox({ x: geometry.pivot.x - geometry.direction.x * 10_000, y: geometry.pivot.y - geometry.direction.y * 10_000 }, { x: geometry.pivot.x + geometry.direction.x * 10_000, y: geometry.pivot.y + geometry.direction.y * 10_000 }, chartWidth, chartHeight);
    const leftFork = clipSegmentToBox({ x: geometry.leftSwing.x - geometry.direction.x * 10_000, y: geometry.leftSwing.y - geometry.direction.y * 10_000 }, { x: geometry.leftSwing.x + geometry.direction.x * 10_000, y: geometry.leftSwing.y + geometry.direction.y * 10_000 }, chartWidth, chartHeight);
    const rightFork = clipSegmentToBox({ x: geometry.rightSwing.x - geometry.direction.x * 10_000, y: geometry.rightSwing.y - geometry.direction.y * 10_000 }, { x: geometry.rightSwing.x + geometry.direction.x * 10_000, y: geometry.rightSwing.y + geometry.direction.y * 10_000 }, chartWidth, chartHeight);
    return [median, leftFork, rightFork].some((segment) => segment != null && distanceToSegment(mouseX, mouseY, segment[0].x, segment[0].y, segment[1].x, segment[1].y) <= tolerance);
}
function hitTestAbcdPattern(drawing, mouseX, mouseY, scales, tolerance) {
    const metrics = calculateAbcdPatternMetrics(drawing, scales);
    if (!metrics) {
        return false;
    }
    const [a, b, c, d] = metrics.points;
    return distanceToSegment(mouseX, mouseY, a.x, a.y, b.x, b.y) <= tolerance
        || distanceToSegment(mouseX, mouseY, b.x, b.y, c.x, c.y) <= tolerance
        || distanceToSegment(mouseX, mouseY, c.x, c.y, d.x, d.y) <= tolerance;
}
function hitTestFibArc(drawing, mouseX, mouseY, scales, tolerance) {
    const geometry = calculateFibArcGeometry(drawing, scales);
    if (!geometry) {
        return false;
    }
    const distanceFromCenter = Math.hypot(mouseX - geometry.center.x, mouseY - geometry.center.y);
    return geometry.radii.some((radius) => Math.abs(distanceFromCenter - radius) <= tolerance);
}
function hitTestFibTimeZone(drawing, mouseX, mouseY, scales, tolerance) {
    const geometry = calculateFibTimeZoneGeometry(drawing, scales);
    if (!geometry) {
        return false;
    }
    return geometry.positions.some((x) => Math.abs(mouseX - x) <= tolerance);
}
function hitTestRegressionChannel(drawing, mouseX, mouseY, scales, plotData, tolerance) {
    const metrics = calculateRegressionChannelMetrics(drawing, scales, plotData ?? []);
    if (!metrics) {
        return false;
    }
    const x1 = Math.min(metrics.startX, metrics.endX) - tolerance;
    const x2 = Math.max(metrics.startX, metrics.endX) + tolerance;
    const y1 = Math.min(metrics.upperStartY, metrics.upperEndY, metrics.lowerStartY, metrics.lowerEndY) - tolerance;
    const y2 = Math.max(metrics.upperStartY, metrics.upperEndY, metrics.lowerStartY, metrics.lowerEndY) + tolerance;
    return pointInRect(mouseX, mouseY, x1, y1, x2 - x1, y2 - y1);
}
function hitTestPositionZones(drawing, mouseX, mouseY, scales, tolerance, mode) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return false;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    return pointInRect(mouseX, mouseY, x - tolerance, y - tolerance, width + tolerance * 2, height + tolerance * 2);
}
// CE24: Drawing Tools Pack 3 hitTest functions
function hitTestAnchoredVWAP(drawing, mouseX, mouseY, scales, options, tolerance) {
    const anchor = drawing.points[0];
    if (!anchor)
        return false;
    const px = toPixel(anchor, scales);
    // Hit test the VWAP line and bands (horizontal lines spanning chart width)
    const plotData = options?.plotData ?? [];
    const anchorTime = anchor.timeMsHint ?? anchor.x;
    const anchorIndex = plotData.findIndex(d => (typeof d.date === 'number' ? d.date : typeof d.date === "number" ? d.date : d.date.getTime()) >= anchorTime);
    if (anchorIndex >= 0 && plotData.length > anchorIndex) {
        let cumulativeVolume = 0;
        let cumulativeVolumePrice = 0;
        for (let i = anchorIndex; i < plotData.length; i++) {
            const bar = plotData[i];
            const typicalPrice = (bar.high + bar.low + bar.close) / 3;
            cumulativeVolume += bar.volume;
            cumulativeVolumePrice += typicalPrice * bar.volume;
        }
        if (cumulativeVolume > 0) {
            const vwapPrice = cumulativeVolumePrice / cumulativeVolume;
            let sumSquaredDiff = 0;
            for (let i = anchorIndex; i < plotData.length; i++) {
                const bar = plotData[i];
                const typicalPrice = (bar.high + bar.low + bar.close) / 3;
                const diff = typicalPrice - vwapPrice;
                sumSquaredDiff += diff * diff * bar.volume;
            }
            const stdDev = Math.sqrt(sumSquaredDiff / cumulativeVolume);
            const levels = [
                vwapPrice,
                vwapPrice + stdDev,
                vwapPrice - stdDev,
                vwapPrice + 2 * stdDev,
                vwapPrice - 2 * stdDev,
            ];
            for (const level of levels) {
                const y = scales.yScale(level);
                if (Math.abs(mouseY - y) <= tolerance && mouseX >= 0 && mouseX <= options.chartWidth) {
                    return true;
                }
            }
        }
    }
    // Also hit test the anchor point
    return Math.hypot(mouseX - px.x, mouseY - px.y) <= tolerance + 4;
}
function hitTestIconMarker(drawing, mouseX, mouseY, scales, tolerance) {
    const point = drawing.points[0];
    if (!point)
        return false;
    const px = toPixel(point, scales);
    const iconSize = drawing.style.size ?? drawing.style.fontSize ?? 24;
    return Math.hypot(mouseX - px.x, mouseY - px.y) <= tolerance + iconSize / 2;
}
function hitTestGhostFeed(drawing, mouseX, mouseY, scales, options, tolerance) {
    const anchor = drawing.points[0];
    if (!anchor)
        return false;
    const px = toPixel(anchor, scales);
    // Hit test the ghost data path if available (validated bridge reader).
    const ghostData = readGhostData(drawing);
    if (ghostData && ghostData.length > 1) {
        const points = ghostData.map(d => toPixel({ x: d.time, y: d.close, timeMsHint: d.time, yPrice: d.close }, scales));
        if (distanceToPolyline(points, mouseX, mouseY) <= tolerance) {
            return true;
        }
    }
    // Hit test anchor point
    return Math.hypot(mouseX - px.x, mouseY - px.y) <= tolerance + 4;
}
function hitTestBarsPattern(drawing, mouseX, mouseY, scales, options, tolerance) {
    if (drawing.points.length < 2)
        return false;
    const pxPoints = drawing.points.map(p => toPixel(p, scales));
    // Hit test the base pattern line
    if (distanceToPolyline(pxPoints.slice(0, 2), mouseX, mouseY) <= tolerance) {
        return true;
    }
    // Hit test matched patterns (validated bridge reader + pre-indexed times).
    const patternMatches = readPatternMatches(drawing);
    if (patternMatches && patternMatches.length > 0) {
        const plotData = options?.plotData ?? [];
        const plotTimes = plotData.map((d) => dateToTimeMs(d.date));
        for (const match of patternMatches) {
            const matchIndex = bisectLeft(plotTimes, match.time);
            if (matchIndex >= 0 && matchIndex + 2 <= plotData.length) {
                const matchPoints = plotData.slice(matchIndex, matchIndex + 2).map(d => {
                    const t = dateToTimeMs(d.date);
                    return toPixel({ x: t, y: d.close, timeMsHint: t, yPrice: d.close }, scales);
                });
                if (distanceToPolyline(matchPoints, mouseX, mouseY) <= tolerance) {
                    return true;
                }
            }
        }
    }
    return false;
}
function hitTestCrossLine(drawing, mouseX, mouseY, scales, options, tolerance) {
    const point = drawing.points[0];
    if (!point)
        return false;
    const px = toPixel(point, scales);
    // Hit test horizontal line (full width)
    if (Math.abs(mouseY - px.y) <= tolerance && mouseX >= 0 && mouseX <= options.chartWidth) {
        return true;
    }
    // Hit test vertical line (full height)
    if (Math.abs(mouseX - px.x) <= tolerance && mouseY >= 0 && mouseY <= options.chartHeight) {
        return true;
    }
    return false;
}
function hitTestDatePriceRange(drawing, mouseX, mouseY, scales, options, tolerance) {
    if (drawing.points.length < 2)
        return false;
    const [p0, p1] = drawing.points;
    const px0 = toPixel(p0, scales);
    const px1 = toPixel(p1, scales);
    // Hit test main line
    if (distanceToSegment(mouseX, mouseY, px0.x, px0.y, px1.x, px1.y) <= tolerance) {
        return true;
    }
    // Hit test horizontal extension lines
    if (Math.abs(mouseY - px0.y) <= tolerance && mouseX >= 0 && mouseX <= px0.x)
        return true;
    if (Math.abs(mouseY - px1.y) <= tolerance && mouseX >= px1.x && mouseX <= options.chartWidth)
        return true;
    // Hit test vertical extension lines
    if (Math.abs(mouseX - px0.x) <= tolerance && mouseY >= 0 && mouseY <= px0.y)
        return true;
    if (Math.abs(mouseX - px1.x) <= tolerance && mouseY >= px1.y && mouseY <= options.chartHeight)
        return true;
    // Hit test anchor points
    if (Math.hypot(mouseX - px0.x, mouseY - px0.y) <= tolerance + 4)
        return true;
    if (Math.hypot(mouseX - px1.x, mouseY - px1.y) <= tolerance + 4)
        return true;
    return false;
}
export function hitTestDrawing(drawing, mouseX, mouseY, scales, options, toleranceOrPointerType = "mouse") {
    if (drawing.visible === false) {
        return false;
    }
    // Phase 5.1: locked and hidden drawings are not interactive
    if (drawing.locked === true || drawing.hidden === true) {
        return false;
    }
    const tolerance = typeof toleranceOrPointerType === "number"
        ? toleranceOrPointerType
        : HIT_TOLERANCE[toleranceOrPointerType];
    const effectiveDrawing = {
        ...drawing,
        style: resolveDrawingStyle(drawing),
    };
    // --- AABB Broad-Phase check (Early Exit) ---
    if (AABB_SAFE_TYPES.has(effectiveDrawing.type)) {
        const bbox = getDrawingBoundingBox(effectiveDrawing, scales, options.chartWidth, options.chartHeight);
        if (bbox) {
            if (mouseX < bbox.minX - tolerance ||
                mouseX > bbox.maxX + tolerance ||
                mouseY < bbox.minY - tolerance ||
                mouseY > bbox.maxY + tolerance) {
                return false;
            }
        }
    }
    const overlayPrimitive = toOverlayPrimitive(effectiveDrawing, scales, {
        chartWidth: options.chartWidth,
        chartHeight: options.chartHeight,
        plotData: options.plotData,
    });
    if (overlayPrimitive) {
        return hitTestOverlayPrimitive(overlayPrimitive, mouseX, mouseY, {
            chartWidth: options.chartWidth,
            chartHeight: options.chartHeight,
            tolerance,
        });
    }
    switch (effectiveDrawing.type) {
        case "ray":
            return hitTestRayLike(effectiveDrawing, mouseX, mouseY, scales, options.chartWidth, options.chartHeight, tolerance, 10_000, true);
        case "extendedLine":
            return hitTestRayLike(effectiveDrawing, mouseX, mouseY, scales, options.chartWidth, options.chartHeight, tolerance, 10_000, false);
        case "polyline": {
            const pixels = effectiveDrawing.points.map((point) => toPixel(point, scales));
            return distanceToPolyline(pixels, mouseX, mouseY) <= tolerance;
        }
        // CE24: Drawing Tools Pack 3
        case "anchoredVwap":
            return hitTestAnchoredVWAP(effectiveDrawing, mouseX, mouseY, scales, options, tolerance);
        case "iconMarker":
            return hitTestIconMarker(effectiveDrawing, mouseX, mouseY, scales, tolerance);
        case "ghostFeed":
            return hitTestGhostFeed(effectiveDrawing, mouseX, mouseY, scales, options, tolerance);
        case "barsPattern":
            return hitTestBarsPattern(effectiveDrawing, mouseX, mouseY, scales, options, tolerance);
        case "crossLine":
            return hitTestCrossLine(effectiveDrawing, mouseX, mouseY, scales, options, tolerance);
        case "datePriceRange":
            return hitTestDatePriceRange(effectiveDrawing, mouseX, mouseY, scales, options, tolerance);
        case "circle":
        case "ellipse": {
            const pts = effectiveDrawing.points;
            if (pts.length < 2)
                return false;
            const p0 = toPixel(pts[0], scales);
            const p1 = toPixel(pts[1], scales);
            const cx = (p0.x + p1.x) / 2;
            const cy = (p0.y + p1.y) / 2;
            const rx = Math.abs(p1.x - p0.x) / 2;
            const ry = Math.abs(p1.y - p0.y) / 2;
            if (rx === 0 || ry === 0)
                return false;
            // P6.4: outline hit — distance to border <= tolerance (cap 12), not filled interior
            const t = Math.min(tolerance, 12);
            const dx = mouseX - cx;
            const dy = mouseY - cy;
            // normalized distance to unit circle, then scale by min radius
            const nx = dx / rx;
            const ny = dy / ry;
            const rNorm = Math.hypot(nx, ny);
            const dist = Math.abs(rNorm - 1) * Math.min(rx, ry);
            return dist <= t;
        }
        case "triangle": {
            const pts = effectiveDrawing.points;
            if (pts.length < 3)
                return false;
            const p1 = toPixel(pts[0], scales);
            const p2 = toPixel(pts[1], scales);
            const p3 = toPixel(pts[2], scales);
            // P6.4: outline hit — min distance to any edge <= tolerance (cap 12)
            const t = Math.min(tolerance, 12);
            const d1 = distanceToSegment(mouseX, mouseY, p1.x, p1.y, p2.x, p2.y);
            const d2 = distanceToSegment(mouseX, mouseY, p2.x, p2.y, p3.x, p3.y);
            const d3 = distanceToSegment(mouseX, mouseY, p3.x, p3.y, p1.x, p1.y);
            return Math.min(d1, d2, d3) <= t;
        }
        default:
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("[drawing] unhandled DrawingToolType in hitTest:", effectiveDrawing.type);
            }
            return false;
    }
}
function getAdaptiveTolerance(scales, baseTolerance) {
    if (scales.xScaleByIndex) {
        const barStep = Math.abs(scales.xScaleByIndex(1) - scales.xScaleByIndex(0));
        if (Number.isFinite(barStep) && barStep > 0 && barStep < 1) {
            return Math.max(baseTolerance, Math.min(24, Math.round(6 / barStep)));
        }
    }
    return baseTolerance;
}
export function getResizeHandleIndex(drawing, mouseX, mouseY, scales, tolerance = 8) {
    const effectiveTolerance = getAdaptiveTolerance(scales, tolerance);
    // For gannSquare the rendered corner (index 1) is constrained to a square aspect ratio
    // and differs from raw points[1] pixel. Use actual geometry corners for hit detection.
    if (drawing.type === "gannSquare" || drawing.type === "gannBox") {
        const geo = calculateGannAdvancedGeometry(drawing, scales);
        if (geo) {
            if (Math.hypot(mouseX - geo.x1, mouseY - geo.y1) <= effectiveTolerance)
                return 0;
            if (Math.hypot(mouseX - geo.x2, mouseY - geo.y2) <= effectiveTolerance)
                return 1;
            return null;
        }
    }
    let closestIndex = null;
    let closestDist = Infinity;
    for (let index = 0; index < drawing.points.length; index += 1) {
        const point = drawing.points[index];
        if (!point) {
            continue;
        }
        const pixel = toPixel(point, scales);
        const dist = Math.hypot(mouseX - pixel.x, mouseY - pixel.y);
        if (dist <= effectiveTolerance && dist < closestDist) {
            closestDist = dist;
            closestIndex = index;
        }
    }
    return closestIndex;
}
export function getTextBoxResizeHandleIndex(drawing, mouseX, mouseY, scales, tolerance = 8) {
    if (!["text", "callout", "priceLabel"].includes(drawing.type))
        return null;
    const point = drawing.points[0];
    if (!point)
        return null;
    const pixel = toPixel(point, scales);
    const fontSize = drawing.style?.fontSize ?? 12;
    const { boxWidth, bgHeight } = computeTextBoxLayout(drawing.text ?? "", drawing.style?.textBoxWidth, drawing.style?.textBoxHeight, fontSize);
    const effectiveTolerance = getAdaptiveTolerance(scales, tolerance);
    const handles = [
        { edge: "nw", x: pixel.x, y: pixel.y },
        { edge: "n", x: pixel.x + boxWidth / 2, y: pixel.y },
        { edge: "ne", x: pixel.x + boxWidth, y: pixel.y },
        { edge: "e", x: pixel.x + boxWidth, y: pixel.y + bgHeight / 2 },
        { edge: "se", x: pixel.x + boxWidth, y: pixel.y + bgHeight },
        { edge: "s", x: pixel.x + boxWidth / 2, y: pixel.y + bgHeight },
        { edge: "sw", x: pixel.x, y: pixel.y + bgHeight },
        { edge: "w", x: pixel.x, y: pixel.y + bgHeight / 2 },
    ];
    for (const handle of handles) {
        if (Math.hypot(mouseX - handle.x, mouseY - handle.y) <= effectiveTolerance) {
            return handle.edge;
        }
    }
    return null;
}
// ─── CE21-DT1: Action handles (delete / clone / lock) ─────────────────────────
/** Visual radius of each action handle circle (px). Larger for touch targets. */
export const ACTION_HANDLE_RADIUS = {
    mouse: 8,
    touch: 12,
    pen: 8,
};
/**
 * Compute the pixel bounding box of a drawing's anchor points.
 * hLine / vLine are treated as spanning the full chart dimension.
 */
export function getDrawingBoundingBox(drawing, scales, chartWidth = 1000, chartHeight = 600) {
    if (drawing.points.length === 0) {
        return null;
    }
    if (drawing.type === "hLine" && drawing.points[0]) {
        const p = toPixel(drawing.points[0], scales);
        return { minX: 0, maxX: chartWidth, minY: p.y, maxY: p.y };
    }
    if (drawing.type === "vLine" && drawing.points[0]) {
        const p = toPixel(drawing.points[0], scales);
        return { minX: p.x, maxX: p.x, minY: 0, maxY: chartHeight };
    }
    if (drawing.type === "circle") {
        const [centerPoint, edgePoint] = drawing.points;
        if (!centerPoint || !edgePoint) {
            return null;
        }
        const center = toPixel(centerPoint, scales);
        const edge = toPixel(edgePoint, scales);
        const radius = Math.hypot(edge.x - center.x, edge.y - center.y);
        if (!Number.isFinite(radius) || radius <= 0) {
            return null;
        }
        return {
            minX: center.x - radius,
            maxX: center.x + radius,
            minY: center.y - radius,
            maxY: center.y + radius,
        };
    }
    // For gannSquare/gannBox/gannSquareFixed use actual rendered corners clamped to the
    // visible chart area so action handles and rubber-band selection stay within viewport.
    if (drawing.type === "gannSquare" || drawing.type === "gannBox" || drawing.type === "gannSquareFixed") {
        const geo = calculateGannAdvancedGeometry(drawing, scales);
        if (geo) {
            return {
                minX: Math.max(0, Math.min(geo.x1, geo.x2)),
                maxX: Math.min(chartWidth, Math.max(geo.x1, geo.x2)),
                minY: Math.max(0, Math.min(geo.y1, geo.y2)),
                maxY: Math.min(chartHeight, Math.max(geo.y1, geo.y2)),
            };
        }
    }
    const pixels = drawing.points.map((pt) => toPixel(pt, scales));
    const xs = pixels.map((p) => p.x);
    const ys = pixels.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    // Include text box dimensions for callout/priceLabel/text so action handles
    // don't float disconnected from the rendered box.
    if ((drawing.type === "callout" || drawing.type === "text" || drawing.type === "priceLabel") && drawing.points[0]) {
        const fontSize = drawing.style?.fontSize ?? 12;
        const { boxWidth, bgHeight } = computeTextBoxLayout(drawing.text ?? "", drawing.style?.textBoxWidth, drawing.style?.textBoxHeight, fontSize);
        const origin = toPixel(drawing.points[0], scales);
        return {
            minX: Math.min(minX, origin.x),
            maxX: Math.max(maxX, origin.x + boxWidth),
            minY: Math.min(minY, origin.y),
            maxY: Math.max(maxY, origin.y + bgHeight),
        };
    }
    return { minX, maxX, minY, maxY };
}
/**
 * Hit-test the three action handles (delete / clone / lock) for a selected drawing.
 * Handle positions relative to the drawing bounding box:
 *   delete → top-right + (14, -14)
 *   clone  → top-right + (32, -14)
 *   lock   → top-left  + (-14, -14)
 *
 * Returns the handle type if the pointer is within the hit radius, otherwise null.
 */
export function hitTestActionHandles(_mouseX, _mouseY, _drawing, _scales, _deviceType = "mouse", _chartWidth, _chartHeight) {
    const bbox = getDrawingBoundingBox(_drawing, _scales, _chartWidth, _chartHeight);
    if (!bbox)
        return null;
    const tolerance = ACTION_HANDLE_RADIUS[_deviceType];
    const handles = [
        { type: "delete", x: bbox.maxX + 14, y: bbox.minY - 14 },
        { type: "clone", x: bbox.maxX + 32, y: bbox.minY - 14 },
        { type: "lock", x: bbox.minX - 14, y: bbox.minY - 14 },
    ];
    for (const h of handles) {
        if (Math.hypot(_mouseX - h.x, _mouseY - h.y) <= tolerance) {
            return h.type;
        }
    }
    return null;
}
/**
 * Returns true if the drawing's bounding box intersects with the given pixel rectangle.
 * Used for rubber-band selection.
 */
export function isDrawingInRect(drawing, rect, scales, chartWidth, chartHeight) {
    const bbox = getDrawingBoundingBox(drawing, scales, chartWidth, chartHeight);
    if (!bbox) {
        return false;
    }
    return !(bbox.maxX < rect.minX || bbox.minX > rect.maxX || bbox.maxY < rect.minY || bbox.minY > rect.maxY);
}
